import { useEffect, useRef, useState } from 'react';
import { useQuery } from 'react-query';

import { Api } from 'shared/types';

interface Props {
  getQrCodeOrderStatus: Api;
  orderId: string | null;
  token: string;
  correlationId: string | null;
}

export const useOrderStatusPolling = ({
  orderId,
  token,
  getQrCodeOrderStatus,
  correlationId
}: Props) => {
  const [isPollingActive, setIsPollingActive] = useState(true);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    timeoutRef.current = setTimeout(() => {
      setIsPollingActive(false);
    }, 120000);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const query = useQuery({
    queryKey: ['orderStatus', orderId],
    queryFn: async () => {
      if (!orderId || !correlationId) {
        throw new Error('Missing required parameters');
      }

      if (!isPollingActive) {
        throw new Error('blocks.renewal.form.error.timeout');
      }

      const urlTemplate = getQrCodeOrderStatus.url;
      const url = urlTemplate.replace('{orderId}', orderId);

      const response = await fetch(url, {
        method: getQrCodeOrderStatus.method,
        headers: {
          Authorization: token,
          correlationId
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch order status');
      }

      const data = await response.json();
      return data.status;
    },
    enabled: !!orderId && !!correlationId && isPollingActive,
    refetchInterval: isPollingActive ? 5000 : false
  });

  useEffect(() => {
    if (query.data === 'CONFIRMED' || query.data === 'FAILED') {
      setIsPollingActive(false);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    }
  }, [query.data]);

  return query;
};
